/**
 * Events data hooks — the flat, uniform read model (every event type renders
 * identically). List + one raw-record fetch for the expandable drawer.
 */
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type {
  EventRunsResponse,
  EventsView,
  PipelineEventDetail,
  PipelineEventListResponse,
} from '../../lib/types.js';

interface EventsListLogic {
  isLoading: boolean;
  data: PipelineEventListResponse | undefined;
  error: Error | null;
}

export const useEventsListLogic = (
  tenantId: string | undefined,
  view: EventsView = 'open',
  unrouted = false,
  /** Off when the Activity tab isn't showing — polling a list nobody is
   *  looking at costs a request every 30s and a re-render with it. */
  active = true,
): EventsListLogic => {
  const query = useQuery<PipelineEventListResponse>({
    queryKey: [...queryKeys.events(tenantId ?? ''), view, unrouted],
    queryFn: () =>
      apiRequest<PipelineEventListResponse>(
        `/api/events?view=${view}${unrouted ? '&unrouted=true' : ''}`,
      ),
    enabled: Boolean(tenantId) && active,
    refetchInterval: 30_000, // observability screen — keep it current
  });
  return { isLoading: query.isLoading, data: query.data, error: query.error };
};

interface EventDetailLogic {
  isLoading: boolean;
  data: PipelineEventDetail | undefined;
  error: Error | null;
}

/** The raw record for the expanded drawer row. */
export const useEventDetailLogic = (
  tenantId: string | undefined,
  eventId: string | null,
): EventDetailLogic => {
  const query = useQuery<PipelineEventDetail>({
    queryKey: queryKeys.event(tenantId ?? '', eventId ?? ''),
    queryFn: () => apiRequest<PipelineEventDetail>(`/api/events/${eventId}`),
    enabled: Boolean(tenantId) && eventId !== null,
  });
  return { isLoading: query.isLoading, data: query.data, error: query.error };
};

interface EventRunsLogic {
  isLoading: boolean;
  data: EventRunsResponse | undefined;
  error: Error | null;
}

/**
 * The workflow runs an event triggered. Polls while anything is still moving —
 * a run parked on a human can sit for days, so there is no point refetching it,
 * but one mid-flight changes within seconds.
 */
export const useEventRunsLogic = (
  tenantId: string | undefined,
  eventId: string | null,
): EventRunsLogic => {
  const query = useQuery<EventRunsResponse>({
    queryKey: [...queryKeys.event(tenantId ?? '', eventId ?? ''), 'runs'],
    queryFn: () => apiRequest<EventRunsResponse>(`/api/events/${eventId ?? ''}/runs`),
    enabled: Boolean(tenantId) && eventId !== null,
    // Keep watching until every run is terminal — planned ('waiting to run'),
    // running, and human-parked runs all still have story to tell. A run
    // mid-execution moves step to step in seconds, so it gets a faster beat.
    refetchInterval: (query) => {
      const runs = query.state.data?.runs ?? [];
      if (runs.some((r) => r.status === 'running')) return 2_000;
      return runs.some((r) => ['waiting to run', 'suspended', 'deferred'].includes(r.status))
        ? 5_000
        : false;
    },
  });
  return { isLoading: query.isLoading, data: query.data, error: query.error };
};
